import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectTranslations } from '../../services/i18n/i18nSlice'
import { setLoading } from '../../services/global/globalSlice'
import { fetchOfaSettings, fetchUpdateOfaSettings, fetchSubjects } from './SuperAdminApi'
import { fetchGroups } from '../UsersScreen/UsersApi'
import Form from 'react-bootstrap/Form'
import OfaGroupSettingModal from './OfaGroupSettingModal'
import OfaPasteModal from './OfaPasteModal'
import { mergeGroupSettings } from './ofaPaste'

const truncateId = (value) => {
    if(!value) return ''
    const str = `${value}`
    return str.length > 14 ? `${str.slice(0,8)}…${str.slice(-4)}` : str
}

const GroupCell = ({ groupId, groupsById }) => {
    if(!groupId) return <span className="text-muted">—</span>
    const name = groupsById?.[groupId]
    return (
        <div title={groupId}>
            {name ? <div>{name}</div> : null}
            <small className="text-muted">{truncateId(groupId)}</small>
        </div>
    )
}

export default function OfaSettingsManager(){
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const [settings, setSettings] = useState({
        esse3_integration_enabled: false,
        esse3_base_url: '',
        extra_field_matricola_id: '',
        group_settings: []
    })
    const [subjects, setSubjects] = useState([])
    const [groupsById, setGroupsById] = useState({})
    const [editingIndex, setEditingIndex] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [pasteOpen, setPasteOpen] = useState(false)

    const load = async () => {
        dispatch(setLoading(true))
        const [settingsRes, subjectsRes, groupsRes] = await Promise.all([
            fetchOfaSettings(),
            fetchSubjects(),
            fetchGroups({ skip: 0, limit: 10000 })
        ])
        if(settingsRes.success){ setSettings(settingsRes.data || settings) }
        if(subjectsRes.success){ setSubjects(subjectsRes.data || []) }
        if(groupsRes.success){
            const map = {}
            for(const g of (groupsRes.data || [])){
                if(g?.group_id) map[g.group_id] = g.name
            }
            setGroupsById(map)
        }
        dispatch(setLoading(false))
    }

    const handleBaseChange = (key, value) => {
        setSettings({ ...settings, [key]: value })
    }

    const openNewGroup = () => {
        setEditingIndex(null)
        setModalOpen(true)
    }

    const openEditGroup = (index) => {
        setEditingIndex(index)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingIndex(null)
    }

    const confirmGroup = (groupSetting) => {
        setSettings(prev => {
            const next = [ ...(prev.group_settings || []) ]
            if(editingIndex === null){
                next.push(groupSetting)
            } else {
                next[editingIndex] = groupSetting
            }
            return { ...prev, group_settings: next }
        })
        closeModal()
    }

    const importPasted = (records) => {
        setSettings(prev => ({ ...prev, group_settings: mergeGroupSettings(prev.group_settings || [], records) }))
        setPasteOpen(false)
    }

    const removeGroupSetting = (index) => {
        setSettings(prev => ({
            ...prev,
            group_settings: (prev.group_settings || []).filter((_,i)=>i!==index)
        }))
    }

    const handleSave = async () => {
        dispatch(setLoading(true))
        const res = await fetchUpdateOfaSettings(settings)
        dispatch(setLoading(false))
        return res
    }

    useEffect(()=>{ load() }, [])

    const editingData = editingIndex !== null ? settings.group_settings?.[editingIndex] : null

    return (
        <section>
            <div className="d-flex flex-wrap">
                <Form.Check
                    type="switch"
                    id="esse3_integration_enabled"
                    label={'Integrazione Esse3 attiva'}
                    checked={!!settings.esse3_integration_enabled}
                    onChange={(e)=>handleBaseChange('esse3_integration_enabled', e.target.checked)}
                    className="me-3"
                />
                <Form.Group className="me-3" controlId="esse3_base_url">
                    <Form.Label>Esse3 Base URL</Form.Label>
                    <Form.Control type="text" value={settings.esse3_base_url || ''} onChange={(e)=>handleBaseChange('esse3_base_url', e.target.value)} />
                </Form.Group>
                <Form.Group className="me-3" controlId="extra_field_matricola_id">
                    <Form.Label>Campo extra Matricola ID</Form.Label>
                    <Form.Control type="text" value={settings.extra_field_matricola_id || ''} onChange={(e)=>handleBaseChange('extra_field_matricola_id', e.target.value)} />
                </Form.Group>
            </div>

            <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5>Configurazioni gruppi</h5>
                    <div>
                        <button className="me-2" onClick={()=>setPasteOpen(true)}>{'Incolla da Excel'}</button>
                        <button onClick={openNewGroup}>{t?.groups?.add_group || 'Aggiungi gruppo'}</button>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table align-middle">
                        <thead>
                            <tr>
                                <th>Group ID</th>
                                <th>Esse3 Test ID</th>
                                <th>Esse3 AA ID</th>
                                <th>Video group</th>
                                <th>Retry group</th>
                                <th>Materie</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {settings.group_settings?.length ? settings.group_settings.map((g, idx)=>(
                                <tr key={idx}>
                                    <td><GroupCell groupId={g.group_id} groupsById={groupsById} /></td>
                                    <td>{g.esse3_test_id || <span className="text-muted">—</span>}</td>
                                    <td>{g.esse3_aa_id || <span className="text-muted">—</span>}</td>
                                    <td><GroupCell groupId={g.video_completion_group_id} groupsById={groupsById} /></td>
                                    <td><GroupCell groupId={g.retry_exam_group_id} groupsById={groupsById} /></td>
                                    <td>{g.subjects?.length || 0}</td>
                                    <td className="text-end">
                                        <button className="me-2" onClick={()=>openEditGroup(idx)}>{t?.actions?.open || 'Modifica'}</button>
                                        <button onClick={()=>removeGroupSetting(idx)}>{t?.actions?.delete || 'Elimina'}</button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted">Nessuna configurazione</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="d-flex justify-content-end">
                <button onClick={handleSave}>{t?.buttons?.edit_save || 'Salva modifiche'}</button>
            </div>

            <OfaGroupSettingModal
                show={modalOpen}
                isNew={editingIndex === null}
                initialData={editingData}
                subjects={subjects}
                onClose={closeModal}
                onConfirm={confirmGroup}
            />

            <OfaPasteModal
                show={pasteOpen}
                groupsById={groupsById}
                subjects={subjects}
                existing={settings.group_settings || []}
                onClose={()=>setPasteOpen(false)}
                onConfirm={importPasted}
            />
        </section>
    )
}
